import type { SpaceId, AuditAction } from '@respaldos/shared';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getAppConfig, isAppConfigured } from './config';
import { initialsFromDisplayName } from './identity';
import { spaceMeta } from './spaceMeta';
import type { ActivityItem, FileItem, SessionUser } from './types';

export type { SessionUser, FileItem, ActivityItem } from './types';

const SESSION_KEY = 'respaldos.session';

export function loadSession(): SessionUser | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SessionUser;
  } catch {
    return null;
  }
}

export function saveSession(user: SessionUser | null) {
  if (!user) localStorage.removeItem(SESSION_KEY);
  else localStorage.setItem(SESSION_KEY, JSON.stringify(user));
}

export function logout() {
  saveSession(null);
}

function requireApiUrl(): string {
  if (!isAppConfigured()) {
    throw new Error('Configuración incompleta: faltan variables Cognito o API.');
  }
  return getAppConfig().apiUrl;
}

async function authHeaders(): Promise<Record<string, string>> {
  const auth = await fetchAuthSession();
  const token = auth.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Sesión Cognito expirada. Vuelva a iniciar sesión.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function apiFetch(path: string, init?: RequestInit) {
  const base = requireApiUrl();
  const headers = await authHeaders();
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      ...headers,
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Error API ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

export async function fetchSpaces(_session: SessionUser) {
  const res = await apiFetch('/spaces');
  return res.spaces;
}

export async function fetchFiles(
  _session: SessionUser,
  spaceId: SpaceId,
  prefixRel = '',
): Promise<FileItem[]> {
  const q = prefixRel
    ? `?prefix=${encodeURIComponent(prefixRel.replace(/^\/+/, '').replace(/\/?$/, '') + '/')}`
    : '';
  const res = await apiFetch(`/spaces/${spaceId}/objects${q}`);
  return (res.items || []) as FileItem[];
}

function friendlyKey(key: string): string {
  const parts = String(key || '')
    .split('/')
    .filter(Boolean);
  return parts[parts.length - 1] || key || '—';
}

const ACTION_ES: Record<string, string> = {
  upload: 'subió',
  download: 'descargó',
  delete: 'eliminó',
  restore: 'restauró',
  mkdir: 'creó la carpeta',
  move: 'movió',
  list: 'visitó',
  archive: 'archivó',
};

function mapActivityEvent(raw: Record<string, unknown>): ActivityItem {
  const who = String(raw.actorName || raw.actorEmail || 'Usuario');
  const action = String(raw.action || 'list') as AuditAction;
  const createdAt = String(raw.createdAt || '');
  let when = createdAt;
  try {
    if (createdAt) {
      const d = new Date(createdAt);
      when = d.toLocaleString('es', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  } catch {
    /* keep ISO */
  }
  return {
    eventId: String(raw.eventId || `${createdAt}-${who}`),
    spaceId: (raw.spaceId as ActivityItem['spaceId']) || 'all',
    initials: initialsFromDisplayName(who, String(raw.actorEmail || '')),
    color: '#010101',
    who,
    action: ACTION_ES[action] || action,
    target: friendlyKey(String(raw.objectKey || raw.detail || '')),
    when,
    kind: action,
  };
}

export async function fetchActivity(_session: SessionUser): Promise<ActivityItem[]> {
  const res = await apiFetch('/activity');
  const events = (res.events || []) as Record<string, unknown>[];
  return events.map(mapActivityEvent);
}

export async function deleteObject(
  _session: SessionUser,
  spaceId: SpaceId,
  key: string,
) {
  await apiFetch(`/spaces/${spaceId}/objects`, {
    method: 'DELETE',
    body: JSON.stringify({ key }),
  });
}

export async function createFolder(
  _session: SessionUser,
  spaceId: SpaceId,
  parentRel: string,
  name: string,
) {
  return apiFetch(`/spaces/${spaceId}/mkdir`, {
    method: 'POST',
    body: JSON.stringify({ path: parentRel, name }),
  });
}

export async function moveObjectPath(
  _session: SessionUser,
  spaceId: SpaceId,
  fromKey: string,
  toKey: string,
) {
  return apiFetch(`/spaces/${spaceId}/move`, {
    method: 'POST',
    body: JSON.stringify({ fromKey, toKey }),
  });
}

export async function requestUploadUrl(
  _session: SessionUser,
  spaceId: SpaceId,
  path: string,
  contentType: string,
): Promise<{ url: string; key: string }> {
  return apiFetch(`/spaces/${spaceId}/upload-url`, {
    method: 'POST',
    body: JSON.stringify({ path, contentType }),
  });
}

export async function requestDownloadUrl(
  _session: SessionUser,
  spaceId: SpaceId,
  key: string,
): Promise<{ url: string; key: string }> {
  return apiFetch(`/spaces/${spaceId}/download-url`, {
    method: 'POST',
    body: JSON.stringify({ key }),
  });
}

/**
 * Upload one file via presigned URL with XHR progress.
 */
export async function uploadFile(
  session: SessionUser,
  spaceId: SpaceId,
  parentRel: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  const parent = parentRel.replace(/^\/+/, '').replace(/\/?$/, '');
  const relativePath = parent ? `${parent}/${file.name}` : file.name;
  const contentType = file.type || 'application/octet-stream';

  const { url } = await requestUploadUrl(session, spaceId, relativePath, contentType);

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && onProgress) {
        onProgress(Math.round((100 * ev.loaded) / ev.total));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Error al subir ${file.name} (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error(`Error de red al subir ${file.name}`));
    xhr.send(file);
  });
}

export function getSpaceMeta(spaceId: SpaceId, currentRelPath = '') {
  return spaceMeta(spaceId, currentRelPath);
}
